import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, FileText, AlertTriangle, Shield,
  ChevronDown, ChevronRight, CheckCircle, XCircle, MinusCircle, BarChart3
} from 'lucide-react';
import ReportIntelligentModal from '../SaisieNotesDevoirs/ReportIntelligentModal';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchEtudiantById } from '../../store/slices/etudiantSlice';
import { useGetNotesDevoirsQuery, NoteDevoir } from '../../services/notesDevoirsApi';
import { useGetNotesExamensQuery, NoteExamen } from '../../services/notesExamensApi';
import { getAnneeAcademique } from '../../services/baseApi';
import { useVerifierEtudiantFraudeQuery } from '../../services/fraudesApi';
import { TableAccessibilityToolbar, useTableA11yStyles } from '../../components/ui/TableAccessibilityToolbar';
import { PageHeader } from '../../components/ui/PageHeader';

interface MatiereResume {
  matiere_id: number;
  matiere_nom: string;
  matiere_code: string;
  devoir1: string | null;
  absent1: boolean;
  devoir2: string | null;
  absent2: boolean;
  moyenne_devoirs: string | null;
  note_partiel: string | null;
  absent_partiel: boolean;
  note_rattrapage: string | null;
  absent_rattrapage: boolean;
}

const ConsultationNotes: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentEtudiant, loading: loadingEtudiant } = useAppSelector((state) => state.etudiants);
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const [expandedSemestre, setExpandedSemestre] = useState<Record<string, boolean>>({ S1: true, S2: true });

  const annee = getAnneeAcademique() || '';
  const etudiantId = id ? parseInt(id) : undefined;

  // Accessibilité : lignes verticales + zoom police (utile pour relecture en jury / personnes âgées)
  const a11y = useTableA11yStyles(0.875);

  // Modal de report intelligent (D1↔D2 ou examen→devoirs) ciblé sur cet étudiant
  const [reportCtx, setReportCtx] = useState<null | {
    matiereId: number; matiereNom: string; matiereCode: string; semestre: 'S1' | 'S2';
  }>(null);
  const { data: fraudeData } = useVerifierEtudiantFraudeQuery(
    { etudiant_id: currentEtudiant?.id },
    { skip: !currentEtudiant?.id }
  );

  React.useEffect(() => {
    if (etudiantId && (!currentEtudiant || currentEtudiant.id !== etudiantId)) {
      dispatch(fetchEtudiantById(etudiantId));
    }
  }, [etudiantId, dispatch, currentEtudiant]);

  // Fetch notes for both semesters
  const { data: devoirsS1 = [], isLoading: loadingDS1 } = useGetNotesDevoirsQuery(
    { etudiant_id: etudiantId, annee_academique: annee, semestre: 'S1' },
    { skip: !etudiantId || !annee }
  );
  const { data: devoirsS2 = [], isLoading: loadingDS2 } = useGetNotesDevoirsQuery(
    { etudiant_id: etudiantId, annee_academique: annee, semestre: 'S2' },
    { skip: !etudiantId || !annee }
  );
  const { data: examensS1 = [], isLoading: loadingES1 } = useGetNotesExamensQuery(
    { etudiant_id: etudiantId, annee_academique: annee, semestre: 'S1' },
    { skip: !etudiantId || !annee }
  );
  const { data: examensS2 = [], isLoading: loadingES2 } = useGetNotesExamensQuery(
    { etudiant_id: etudiantId, annee_academique: annee, semestre: 'S2' },
    { skip: !etudiantId || !annee }
  );

  const isLoading = loadingEtudiant || loadingDS1 || loadingDS2 || loadingES1 || loadingES2;

  // Build résumé per semestre
  const buildResume = (devoirs: NoteDevoir[], examens: NoteExamen[]): MatiereResume[] => {
    const map = new Map<string, MatiereResume>();

    for (const d of devoirs) {
      map.set(d.matiere_nom, {
        matiere_id: d.matiere,
        matiere_nom: d.matiere_nom,
        matiere_code: d.matiere_code,
        devoir1: d.note1,
        absent1: d.absent1,
        devoir2: d.note2,
        absent2: d.absent2,
        moyenne_devoirs: d.moyenne_devoirs,
        note_partiel: null,
        absent_partiel: false,
        note_rattrapage: null,
        absent_rattrapage: false,
      });
    }

    for (const e of examens) {
      const existing = map.get(e.matiere_nom);
      if (existing) {
        if (e.est_rattrapage) {
          existing.note_rattrapage = e.note_examen;
          existing.absent_rattrapage = e.absent;
        } else {
          existing.note_partiel = e.note_examen;
          existing.absent_partiel = e.absent;
        }
      } else {
        map.set(e.matiere_nom, {
          matiere_id: e.matiere,
          matiere_nom: e.matiere_nom,
          matiere_code: e.matiere_code,
          devoir1: null, absent1: false,
          devoir2: null, absent2: false,
          moyenne_devoirs: null,
          note_partiel: e.est_rattrapage ? null : e.note_examen,
          absent_partiel: e.est_rattrapage ? false : e.absent,
          note_rattrapage: e.est_rattrapage ? e.note_examen : null,
          absent_rattrapage: e.est_rattrapage ? e.absent : false,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.matiere_nom.localeCompare(b.matiere_nom));
  };

  const resumeS1 = useMemo(() => buildResume(devoirsS1, examensS1), [devoirsS1, examensS1]);
  const resumeS2 = useMemo(() => buildResume(devoirsS2, examensS2), [devoirsS2, examensS2]);

  const renderNote = (note: string | null, absent: boolean) => {
    if (absent) return <span className="text-red-500 font-medium text-xs">ABS</span>;
    if (note === null || note === undefined) return <span className="text-gray-400">—</span>;
    const val = parseFloat(note);
    return (
      <span className={`font-semibold ${val >= 10 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
        {val.toFixed(2)}
      </span>
    );
  };

  // Détecte si on peut proposer un report pour cette matière (D1 OU D2 manquant, ou les deux + examen présent)
  const reportPossible = (r: MatiereResume): boolean => {
    const hasD1 = r.devoir1 !== null || r.absent1;
    const hasD2 = r.devoir2 !== null || r.absent2;
    const hasExam = r.note_partiel !== null || r.absent_partiel;
    if (hasD1 && hasD2) return false;
    if (!hasD1 && !hasD2) return hasExam;
    return true; // un seul devoir → D1↔D2
  };

  const renderSemestreTable = (resume: MatiereResume[], sem: string) => {
    const isExpanded = expandedSemestre[sem] ?? true;
    const totalMatieres = resume.length;
    const hasAnyNote = resume.some(r => r.devoir1 !== null || r.devoir2 !== null || r.note_partiel !== null || r.note_rattrapage !== null);

    return (
      <Card className="overflow-hidden">
        <button
          onClick={() => setExpandedSemestre(prev => ({ ...prev, [sem]: !prev[sem] }))}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Semestre {sem === 'S1' ? '1' : '2'}
            </h3>
            <Badge variant={totalMatieres > 0 ? 'info' : 'default'}>
              {totalMatieres} matière{totalMatieres > 1 ? 's' : ''}
            </Badge>
          </div>
          {!hasAnyNote && totalMatieres === 0 && (
            <span className="text-sm text-gray-400">Aucune note enregistrée</span>
          )}
        </button>

        {isExpanded && totalMatieres > 0 && (
          <div className="overflow-x-auto">
            <table className={`w-full ${a11y.tableClass}`} style={a11y.tableStyle}>
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700/50">
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300">Matière</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300">Dev. 1</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300">Dev. 2</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-blue-700 dark:text-blue-400">Moy. Dev.</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-purple-700 dark:text-purple-400">Partiel</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-orange-700 dark:text-orange-400">Rattrapage</th>
                  <th className="text-center px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-300">Statut</th>
                </tr>
              </thead>
              <tbody>
                {resume.map((r, i) => {
                  const moyDev = r.moyenne_devoirs ? parseFloat(r.moyenne_devoirs) : null;
                  const notePartiel = r.note_partiel ? parseFloat(r.note_partiel) : null;
                  const noteRatt = r.note_rattrapage ? parseFloat(r.note_rattrapage) : null;

                  // Calcul note finale simplifiée : 40% devoirs + 60% examen
                  let noteFinale: number | null = null;
                  const examNote = noteRatt !== null ? noteRatt : notePartiel;
                  if (moyDev !== null && examNote !== null) {
                    noteFinale = moyDev * 0.4 + examNote * 0.6;
                  }

                  let statut: 'validee' | 'non_validee' | 'incomplete' = 'incomplete';
                  if (noteFinale !== null) {
                    statut = noteFinale >= 10 ? 'validee' : 'non_validee';
                  }

                  return (
                    <tr key={r.matiere_nom} className={`border-t border-gray-200 dark:border-gray-700 ${i % 2 === 0 ? '' : 'bg-gray-50/50 dark:bg-gray-800/30'}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{r.matiere_nom}</div>
                        <div className="text-xs text-gray-500">{r.matiere_code}</div>
                      </td>
                      <td className="text-center px-3 py-2.5">{renderNote(r.devoir1, r.absent1)}</td>
                      <td className="text-center px-3 py-2.5">{renderNote(r.devoir2, r.absent2)}</td>
                      <td className="text-center px-3 py-2.5 font-semibold">
                        {renderNote(r.moyenne_devoirs, false)}
                      </td>
                      <td className="text-center px-3 py-2.5">{renderNote(r.note_partiel, r.absent_partiel)}</td>
                      <td className="text-center px-3 py-2.5">
                        {r.note_rattrapage !== null || r.absent_rattrapage
                          ? renderNote(r.note_rattrapage, r.absent_rattrapage)
                          : <span className="text-gray-300">—</span>
                        }
                      </td>
                      <td className="text-center px-3 py-2.5">
                        <div className="flex items-center justify-center gap-2">
                          {statut === 'validee' && (
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                              <CheckCircle size={14} />
                              <span className="text-xs font-medium">{noteFinale?.toFixed(2)}</span>
                            </span>
                          )}
                          {statut === 'non_validee' && (
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                              <XCircle size={14} />
                              <span className="text-xs font-medium">{noteFinale?.toFixed(2)}</span>
                            </span>
                          )}
                          {statut === 'incomplete' && (
                            <span className="inline-flex items-center gap-1 text-gray-400">
                              <MinusCircle size={14} />
                              <span className="text-xs">En cours</span>
                            </span>
                          )}
                          {reportPossible(r) && (
                            <button
                              onClick={() => setReportCtx({
                                matiereId: r.matiere_id, matiereNom: r.matiere_nom,
                                matiereCode: r.matiere_code, semestre: sem as 'S1' | 'S2',
                              })}
                              title="Reporter D1↔D2 ou Examen→Devoirs pour cet étudiant"
                              className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                            >
                              <BarChart3 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {isExpanded && totalMatieres === 0 && (
          <div className="px-4 pb-4 text-center text-gray-400 text-sm">
            Aucune note pour ce semestre
          </div>
        )}
      </Card>
    );
  };

  if (isLoading && !currentEtudiant) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!currentEtudiant) {
    return (
      <div className="text-center p-8">
        <AlertTriangle size={48} className="mx-auto text-yellow-500 mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Étudiant introuvable</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-blue-500 hover:underline">Retour</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header (unifié — refonte vague 3 lot 1) */}
      <PageHeader
        title={`Notes de ${currentEtudiant.nom} ${currentEtudiant.prenoms}`}
        description={
          <>
            <span className="font-mono">{currentEtudiant.matricule}</span>
            {' · '}{currentEtudiant.classe || 'N/A'}
            {' · '}{annee || 'Année non sélectionnée'}
          </>
        }
        back={
          <button onClick={() => navigate(`/etudiants/${id}`)}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
            aria-label="Retour à la fiche">
            <ArrowLeft size={18} />
          </button>
        }
        actions={<TableAccessibilityToolbar />}
      />

      {/* Fraud Alert Banner */}
      {fraudeData?.has_fraude && (
        <div className="bg-red-50 dark:bg-red-900/30 border-2 border-red-400 dark:border-red-600 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Shield className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={22} />
            <div className="flex-1">
              <h4 className="font-bold text-red-800 dark:text-red-300 text-base">
                ATTENTION — Cet etudiant a {fraudeData.count} cas de fraude actif{fraudeData.count > 1 ? 's' : ''}
              </h4>
              <ul className="mt-2 space-y-1">
                {fraudeData.cas_actifs.map((cas) => (
                  <li key={cas.id} className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
                    <AlertTriangle size={14} className="flex-shrink-0" />
                    <span>
                      <strong>{cas.reference}</strong> — {cas.type_fraude_display} — {cas.matiere_nom} — <em>{cas.statut_display}</em>
                    </span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/fraudes')}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline font-medium"
              >
                Voir tous les cas de fraude &rarr;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student info card */}
      <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700 rounded-xl">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center text-lg font-bold">
          {currentEtudiant.nom.charAt(0)}
        </div>
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
          <div>
            <span className="text-xs text-blue-600 dark:text-blue-400">Niveau</span>
            <div className="font-semibold text-gray-900 dark:text-white">{currentEtudiant.niveau}</div>
          </div>
          <div>
            <span className="text-xs text-blue-600 dark:text-blue-400">Classe</span>
            <div className="font-semibold text-gray-900 dark:text-white">{currentEtudiant.classe}</div>
          </div>
          <div>
            <span className="text-xs text-blue-600 dark:text-blue-400">Parcours</span>
            <div className="font-semibold text-gray-900 dark:text-white">{currentEtudiant.parcours || '—'}</div>
          </div>
          <div>
            <span className="text-xs text-blue-600 dark:text-blue-400">Option</span>
            <div className="font-semibold text-gray-900 dark:text-white">{currentEtudiant.option || '—'}</div>
          </div>
          <div>
            <span className="text-xs text-blue-600 dark:text-blue-400">Pôle</span>
            <div className="font-semibold text-gray-900 dark:text-white">{currentEtudiant.pole || '—'}</div>
          </div>
        </div>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Matières S1', value: resumeS1.length, icon: BookOpen, color: 'blue' },
          { label: 'Matières S2', value: resumeS2.length, icon: FileText, color: 'purple' },
          { label: 'Validées', value: [...resumeS1, ...resumeS2].filter(r => {
            const moyDev = r.moyenne_devoirs ? parseFloat(r.moyenne_devoirs) : null;
            const exam = r.note_rattrapage ? parseFloat(r.note_rattrapage) : (r.note_partiel ? parseFloat(r.note_partiel) : null);
            return moyDev !== null && exam !== null && (moyDev * 0.4 + exam * 0.6) >= 10;
          }).length, icon: CheckCircle, color: 'green' },
          { label: 'Non validées', value: [...resumeS1, ...resumeS2].filter(r => {
            const moyDev = r.moyenne_devoirs ? parseFloat(r.moyenne_devoirs) : null;
            const exam = r.note_rattrapage ? parseFloat(r.note_rattrapage) : (r.note_partiel ? parseFloat(r.note_partiel) : null);
            return moyDev !== null && exam !== null && (moyDev * 0.4 + exam * 0.6) < 10;
          }).length, icon: XCircle, color: 'red' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className={`p-3 rounded-xl border border-${color}-200 dark:border-${color}-700 bg-${color}-50 dark:bg-${color}-900/20`}>
            <div className="flex items-center gap-2">
              <Icon size={16} className={`text-${color}-500`} />
              <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
            </div>
            <div className={`text-2xl font-bold text-${color}-600 dark:text-${color}-400 mt-1`}>{value}</div>
          </div>
        ))}
      </div>

      {isLoading && (
        <div className="flex justify-center p-4">
          <Spinner size="md" />
        </div>
      )}

      {/* Tables par semestre */}
      {renderSemestreTable(resumeS1, 'S1')}
      {renderSemestreTable(resumeS2, 'S2')}

      {/* Modal Report Intelligent ciblé sur cet étudiant uniquement */}
      {reportCtx && currentEtudiant && (
        <ReportIntelligentModal
          open={!!reportCtx}
          onClose={() => setReportCtx(null)}
          matiereId={reportCtx.matiereId}
          matiereNom={reportCtx.matiereNom}
          matiereCode={reportCtx.matiereCode}
          classes={[currentEtudiant.classe || '']}
          anneeAcademique={annee}
          semestre={reportCtx.semestre}
          selectionInitiale={[currentEtudiant.id]}
        />
      )}
    </div>
  );
};

export default ConsultationNotes;
